(() => {
  'use strict';

  const storageKey = 'yafico-lighting-favorites-v1';
  const contexts = new WeakMap();
  let memory = [];
  const copy = {
    nl: {
      group: 'Bewaren en delen', save: 'Bewaar', saved: 'Bewaard', share: 'Deel', list: 'Bewaarde objecten',
      title: 'Jouw bewaarde lichtobjecten', empty: 'Nog geen lichtobjecten bewaard. Gebruik Bewaar bij een product dat je mooi vindt.',
      remove: 'Verwijder uit bewaard', close: 'Sluiten', storage: 'Bewaren op dit apparaat is niet beschikbaar. Je selectie blijft alleen op deze pagina beschikbaar.',
      note: 'Bewaard in deze browser. Je hoeft geen account aan te maken. De gekozen uitvoering blijft bij de link horen.',
      shareTitle: 'Deel jouw lichtobject', shareNote: 'Deze link opent precies de gekozen uitvoering.',
      link: 'Productlink', copy: 'Kopieer link', copied: 'Link gekopieerd.', manual: 'Kopiëren lukt hier niet automatisch. Selecteer en kopieer de link hieronder.',
      added: 'Toegevoegd aan jouw bewaarde objecten.', removed: 'Verwijderd uit jouw bewaarde objecten.', full: 'Je kunt maximaal 40 uitvoeringen bewaren. Verwijder eerst een object.',
      shared: 'Delen geopend.'
    },
    de: {
      group: 'Merken und teilen', save: 'Merken', saved: 'Gemerkt', share: 'Teilen', list: 'Gemerkte Objekte',
      title: 'Deine gemerkten Lichtobjekte', empty: 'Noch keine Lichtobjekte gemerkt. Nutze Merken bei einem Produkt, das dir gefällt.',
      remove: 'Aus Merkliste entfernen', close: 'Schließen', storage: 'Speichern auf diesem Gerät ist nicht verfügbar. Deine Auswahl bleibt nur auf dieser Seite erhalten.',
      note: 'In diesem Browser gespeichert. Du brauchst kein Konto. Die gewählte Ausführung bleibt im Link enthalten.',
      shareTitle: 'Dein Lichtobjekt teilen', shareNote: 'Dieser Link öffnet genau die gewählte Ausführung.',
      link: 'Produktlink', copy: 'Link kopieren', copied: 'Link kopiert.', manual: 'Automatisches Kopieren ist hier nicht möglich. Wähle und kopiere den Link unten.',
      added: 'Zu deinen gemerkten Objekten hinzugefügt.', removed: 'Aus deinen gemerkten Objekten entfernt.', full: 'Du kannst höchstens 40 Ausführungen merken. Entferne zuerst ein Objekt.',
      shared: 'Teilen geöffnet.'
    }
  };

  function escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  }

  function baseUrl() {
    try {
      const base = new URL(window.location.href);
      return /^https?:$/.test(base.protocol) ? base : null;
    } catch {
      return null;
    }
  }

  function safeUrl(value, preview = false) {
    const base = baseUrl();
    if (!base || typeof value !== 'string' || value.length > 1500) return '';
    try {
      const target = new URL(value, base);
      const nativeProduct = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+\/?$/i.test(target.pathname);
      const previewProduct = preview && target.pathname === base.pathname && /^(?:0[1-9]|1\d|20)$/.test(target.searchParams.get('product') || '');
      if (target.origin !== base.origin || (!nativeProduct && !previewProduct)) return '';
      target.hash = '';
      const variant = target.searchParams.get('variant');
      if (variant && !/^(?:[1-9]\d*|preview-07-(?:bone-white|natural-amber))$/.test(variant)) return '';
      const model = previewProduct ? target.searchParams.get('product') : '';
      const language = previewProduct ? target.searchParams.get('lang') : '';
      target.search = '';
      if (model) target.searchParams.set('product', model);
      if (language === 'nl' || language === 'de') target.searchParams.set('lang', language);
      if (variant) target.searchParams.set('variant', variant);
      return target.href;
    } catch {
      return '';
    }
  }

  function safeImage(value) {
    const base = baseUrl();
    if (!base || typeof value !== 'string' || value.length > 1500) return '';
    try {
      const target = new URL(value, base);
      return target.origin === base.origin || (target.protocol === 'https:' && target.hostname === 'cdn.shopify.com') ? target.href : '';
    } catch {
      return '';
    }
  }

  function selection(settings, product) {
    if (!/^(?:0[1-9]|1\d|20)$/.test(product?.id || '') || typeof settings.productUrl !== 'function') return null;
    const url = safeUrl(settings.productUrl(product), settings.preview === true);
    if (!url) return null;
    const variant = new URL(url).searchParams.get('variant') || 'default';
    return {
      key: `${product.id}:${variant}`, url, name: String(product.name || '').slice(0, 100),
      color: String(product.colorLabel || product.material || '').slice(0, 120),
      image: safeImage(settings.thumbnail?.(product, 0) || settings.image?.(product, 0) || '')
    };
  }

  function cleanEntries(values, settings) {
    if (!Array.isArray(values)) return [];
    const entries = [];
    const keys = new Set();
    for (const value of values.slice(0, 80)) {
      if (!value || typeof value.name !== 'string' || !value.name.trim()) continue;
      const match = String(value.key || '').match(/^((?:0[1-9]|1\d|20)):(default|[1-9]\d*|preview-07-(?:bone-white|natural-amber))$/);
      const url = safeUrl(value.url, settings.preview === true);
      if (!match || !url || keys.has(value.key)) continue;
      if ((new URL(url).searchParams.get('variant') || 'default') !== match[2]) continue;
      keys.add(value.key);
      entries.push({ key: value.key, url, name: value.name.slice(0, 100), color: String(value.color || '').slice(0, 120), image: safeImage(value.image) });
      if (entries.length === 40) break;
    }
    return entries;
  }

  function readEntries(settings) {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return { entries: stored ? cleanEntries(JSON.parse(stored), settings) : memory.slice(), persistent: true };
    } catch {
      return { entries: memory.slice(), persistent: false };
    }
  }

  function writeEntries(settings, entries) {
    memory = cleanEntries(entries, settings);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(memory));
      return true;
    } catch {
      return false;
    }
  }

  function icon(kind) {
    const paths = {
      save: '<path d="M6 3h12v18l-6-4-6 4V3Z"/>',
      share: '<path d="M12 16V3m-4 4 4-4 4 4M5 12v8h14v-8"/>',
      list: '<path d="M5 6h14M5 12h14M5 18h14"/>'
    };
    return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[kind]}</svg>`;
  }

  function markup(settings, product) {
    const current = selection(settings, product);
    if (!current) return '';
    const words = copy[settings.locale] || copy.nl;
    const { entries } = readEntries(settings);
    const saved = entries.some(entry => entry.key === current.key);
    return `<div class="yp-product-tools" role="group" aria-label="${words.group}"><button type="button" data-tools-action="save" aria-pressed="${saved}">${icon('save')}<span data-tools-save-label>${saved ? words.saved : words.save}</span></button><button type="button" data-tools-action="share">${icon('share')}<span>${words.share}</span></button><button type="button" data-tools-action="list">${icon('list')}<span>${words.list} (<span data-tools-count>${entries.length}</span>)</span></button><p class="yp-tools-status" data-tools-status role="status" aria-live="polite"></p></div>`;
  }

  function savedMarkup(settings, entries, persistent = true) {
    const words = copy[settings.locale] || copy.nl;
    return `<header><h2 id="yp-tools-dialog-title">${words.title}</h2><button type="button" data-tools-action="close" aria-label="${words.close}">×</button></header>${entries.length ? `<ul class="yp-saved-list">${entries.map(entry => `<li><a href="${escape(entry.url)}">${entry.image ? `<img src="${escape(entry.image)}" alt="" width="72" height="80" loading="lazy">` : ''}<span><strong>${escape(entry.name)}</strong><small>${escape(entry.color)}</small></span></a><button type="button" data-tools-action="remove" data-tools-key="${escape(entry.key)}" aria-label="${words.remove} · ${escape(entry.name)}">×</button></li>`).join('')}</ul>` : `<p>${words.empty}</p>`}<p class="yp-tools-note">${persistent ? words.note : words.storage}</p>`;
  }

  function shareMarkup(settings, current, manual = false) {
    const words = copy[settings.locale] || copy.nl;
    return `<header><h2 id="yp-tools-dialog-title">${words.shareTitle}</h2><button type="button" data-tools-action="close" aria-label="${words.close}">×</button></header><p>${manual ? words.manual : words.shareNote}</p><label class="yp-share-label">${words.link}<input data-tools-link type="url" readonly value="${escape(current.url)}"></label><button type="button" class="yp-tools-copy" data-tools-action="copy">${words.copy}</button><p data-tools-dialog-status role="status" aria-live="polite"></p>`;
  }

  function status(context, message) {
    const target = context.settings.root.querySelector('[data-tools-status]');
    if (target) target.textContent = message;
  }

  function refresh(context) {
    const root = context.settings.root;
    const { entries } = readEntries(context.settings);
    const saved = entries.some(entry => entry.key === context.current.key);
    root.querySelector('[data-tools-action="save"]')?.setAttribute('aria-pressed', String(saved));
    const label = root.querySelector('[data-tools-save-label]');
    const count = root.querySelector('[data-tools-count]');
    if (label) label.textContent = saved ? context.words.saved : context.words.save;
    if (count) count.textContent = String(entries.length);
  }

  function openDialog(context, mode, trigger, manual = false) {
    context.dialog?.close();
    context.dialog?.remove();
    const dialog = document.createElement('dialog');
    dialog.className = 'yp-tools-dialog';
    dialog.setAttribute('aria-labelledby', 'yp-tools-dialog-title');
    const state = readEntries(context.settings);
    dialog.innerHTML = mode === 'list' ? savedMarkup(context.settings, state.entries, state.persistent) : shareMarkup(context.settings, context.current, manual);
    context.mode = mode;
    context.dialog = dialog;
    context.trigger = trigger;
    context.settings.root.append(dialog);
    dialog.addEventListener('close', () => {
      if (context.trigger?.isConnected) context.trigger.focus();
    });
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });
    dialog.showModal();
    if (manual) dialog.querySelector('[data-tools-link]')?.select();
  }

  async function share(context, trigger) {
    const title = `${context.current.name}${context.current.color ? ` · ${context.current.color}` : ''} | Yafico Lighting`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url: context.current.url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    openDialog(context, 'share', trigger);
  }

  async function copyLink(context) {
    const message = context.dialog?.querySelector('[data-tools-dialog-status]');
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(context.current.url);
      if (message) message.textContent = context.words.copied;
    } catch {
      if (message) message.textContent = context.words.manual;
      context.dialog?.querySelector('[data-tools-link]')?.select();
    }
  }

  function mount(settings, product) {
    const root = settings.root;
    if (!root?.addEventListener) return;
    let context = contexts.get(root);
    if (!context) {
      context = {};
      contexts.set(root, context);
      root.addEventListener('click', async event => {
        const trigger = event.target.closest?.('[data-tools-action]');
        if (!trigger || !root.contains(trigger) || !context.current) return;
        event.preventDefault();
        const action = trigger.dataset.toolsAction;
        if (action === 'close') return context.dialog?.close();
        if (action === 'share') return share(context, trigger);
        if (action === 'copy') return copyLink(context);
        if (action === 'list') return openDialog(context, 'list', trigger);
        const { entries } = readEntries(context.settings);
        const key = action === 'remove' ? trigger.dataset.toolsKey : context.current.key;
        const exists = entries.some(entry => entry.key === key);
        if (action !== 'remove' && action !== 'save') return;
        if (action === 'remove' && !exists) return;
        if (!exists && entries.length >= 40) return status(context, context.words.full);
        const next = exists ? entries.filter(entry => entry.key !== key) : entries.concat(context.current);
        const persistent = writeEntries(context.settings, next);
        refresh(context);
        status(context, persistent ? exists ? context.words.removed : context.words.added : context.words.storage);
        if (action === 'remove' && context.dialog?.open) {
          context.dialog.innerHTML = savedMarkup(context.settings, next, persistent);
          context.dialog.querySelector('[data-tools-action="close"]')?.focus();
        }
      });
    }
    context.dialog?.close();
    context.dialog?.remove();
    Object.assign(context, { settings, current: selection(settings, product), words: copy[settings.locale] || copy.nl, dialog: null });
  }

  window.YaficoProductTools = { markup, mount, selection, cleanEntries, readEntries, writeEntries, savedMarkup, shareMarkup, copy };
})();
